import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle, RefreshCw,
  Loader2, Search, X, Database, Users, CreditCard, Link2
} from 'lucide-react';

const DataIntegrityTab = () => {
  const [integrityResult, setIntegrityResult] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Restore-from-orphaned-bookings state
  const [restoreStudentId, setRestoreStudentId] = useState('');
  const [restoreUserId, setRestoreUserId] = useState('');
  const [restoreStudentName, setRestoreStudentName] = useState('');
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoring, setRestoring] = useState(false);

  // Duplicate children cleanup state
  const [dupResult, setDupResult] = useState(null);
  const [dupLoading, setDupLoading] = useState(false);
  const [keepOverrides, setKeepOverrides] = useState({}); // { group_key: child_id }

  // Stripe reconciliation state
  const [stripeRecon, setStripeRecon] = useState(null);
  const [stripeReconLoading, setStripeReconLoading] = useState(false);
  const [reconDays, setReconDays] = useState(90);
  const [reconMinAmount, setReconMinAmount] = useState(5);
  const [fixingPi, setFixingPi] = useState(null);
  const [rebuildingPi, setRebuildingPi] = useState(null);

  // Silent Imported-Student Recovery state
  const [recPe, setRecPe] = useState('');
  const [recCus, setRecCus] = useState('');
  const [recPi, setRecPi] = useState('');
  const [recDryRun, setRecDryRun] = useState(true);
  const [recRunning, setRecRunning] = useState(false);
  const [recResult, setRecResult] = useState(null);

  // Diagnose-student state
  const [diagInput, setDiagInput] = useState('');
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  const [fixNullsLoading, setFixNullsLoading] = useState(false);

  const runFixNulls = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('This will set amount=0 on any booking with a null amount. Safe to run. Continue?')) return;
    setFixNullsLoading(true);
    try {
      const res = await api.post('/admin/data/fix-null-booking-amounts');
      toast.success(`Fixed ${res.data.modified_count} booking(s) with null amounts.`);
    } catch (error) {
      toast.error(`Fix failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setFixNullsLoading(false);
    }
  };

  const runDiagnose = async () => {
    const id = diagInput.trim();
    if (!id) {
      toast.error('Paste a child_id (UUID) to diagnose');
      return;
    }
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const res = await api.get(`/admin/data/diagnose-student/${id}`);
      setDiagResult(res.data);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(`Diagnose failed: ${detail}`);
      setDiagResult({ error: detail });
    } finally {
      setDiagLoading(false);
    }
  };

  const recoverImportedStudent = async () => {
    if (!recPe.trim() && !recCus.trim() && !recPi.trim()) {
      toast.error('Provide at least one of: pending_enrollment_id, stripe_customer_id, or payment_intent_id');
      return;
    }
    if (!recDryRun) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        'This will CREATE missing user/child/booking rows for this parent silently.\n\n' +
        'No emails will be sent. Continue?'
      );
      if (!ok) return;
    }
    setRecRunning(true);
    setRecResult(null);
    try {
      const res = await api.post('/admin/data/recover-imported-student', {
        pending_enrollment_id: recPe.trim() || undefined,
        stripe_customer_id: recCus.trim() || undefined,
        stripe_payment_intent_id: recPi.trim() || undefined,
        dry_run: recDryRun,
      });
      setRecResult(res.data);
      toast.success(
        recDryRun
          ? 'Dry run complete — review the plan below'
          : `Recovery complete — ${res.data.bookings_created} booking(s) created`
      );
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(`Recovery failed: ${detail}`);
      setRecResult({ error: detail });
    } finally {
      setRecRunning(false);
    }
  };

  // Re-attribute bookings by name state
  const [reattribStudent, setReattribStudent] = useState('');
  const [reattribParentEmail, setReattribParentEmail] = useState('');
  const [reattribLoading, setReattribLoading] = useState(false);
  const [reattribResult, setReattribResult] = useState(null);

  // Orphaned bookings cleanup state
  const [orphans, setOrphans] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphanSelected, setOrphanSelected] = useState(new Set());
  const [orphansDeleting, setOrphansDeleting] = useState(false);

  // Orphaned Stripe SUBSCRIPTIONS (live on Stripe but not linked to a booking)
  const [orphanSubs, setOrphanSubs] = useState(null);
  const [orphanSubsLoading, setOrphanSubsLoading] = useState(false);
  const [linkSub, setLinkSub] = useState({ booking_id: '', stripe_subscription_id: '' });
  const [linkSubSaving, setLinkSubSaving] = useState(false);

  const fetchOrphanSubs = async () => {
    setOrphanSubsLoading(true);
    try {
      const res = await api.get('/payments/subscriptions/orphans');
      setOrphanSubs(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load orphan subscriptions');
    } finally {
      setOrphanSubsLoading(false);
    }
  };

  const submitLinkSub = async () => {
    if (!linkSub.booking_id.trim() || !linkSub.stripe_subscription_id.trim()) {
      toast.error('Both Booking ID and Stripe Subscription ID are required');
      return;
    }
    setLinkSubSaving(true);
    try {
      const res = await api.post('/payments/subscriptions/link-to-booking', {
        booking_id: linkSub.booking_id.trim(),
        stripe_subscription_id: linkSub.stripe_subscription_id.trim(),
      });
      toast.success(res.data.message || 'Subscription linked');
      setLinkSub({ booking_id: '', stripe_subscription_id: '' });
      fetchOrphanSubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link subscription');
    } finally {
      setLinkSubSaving(false);
    }
  };

  const fetchOrphans = async () => {
    setOrphansLoading(true);
    try {
      const res = await api.get('/admin/data/orphaned-bookings');
      setOrphans(res.data?.orphans || []);
      setOrphanSelected(new Set());
      toast.success(`Found ${res.data?.count || 0} orphaned booking(s)`);
    } catch (err) {
      toast.error('Failed to load orphans: ' + (err.response?.data?.detail || err.message));
    } finally {
      setOrphansLoading(false);
    }
  };

  const toggleOrphan = (id) => {
    setOrphanSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllOrphans = () => {
    setOrphanSelected((prev) =>
      prev.size === orphans.length ? new Set() : new Set(orphans.map((o) => o.id))
    );
  };

  const downloadOrphansCsv = () => {
    if (!orphans.length) return;
    const headers = ['id','student_name','class_name','amount','payment_status','status','created_at','user_id','student_id'];
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [headers.join(',')].concat(
      orphans.map((o) => headers.map((h) => escape(o[h])).join(','))
    );
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orphaned-bookings-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteSelectedOrphans = async () => {
    if (orphanSelected.size === 0) {
      toast.error('Select at least one row');
      return;
    }
    if (!window.confirm(
      `Delete ${orphanSelected.size} orphaned booking(s)?\n\nThis cannot be undone (use "Download CSV" first if you want a record). Only bookings still missing student_id/user_id will be deleted.`
    )) return;
    setOrphansDeleting(true);
    try {
      const res = await api.post('/admin/data/orphaned-bookings/delete', {
        ids: Array.from(orphanSelected),
        confirm: true,
      });
      toast.success(`Deleted ${res.data?.deleted_count || 0} (skipped ${res.data?.skipped_count || 0}).`);
      // Reload
      fetchOrphans();
    } catch (err) {
      toast.error('Delete failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setOrphansDeleting(false);
    }
  };

  const reattributeByName = async (dryRun) => {
    if (!reattribStudent || !reattribParentEmail) {
      toast.error('Student name and parent email are required');
      return;
    }
    if (!dryRun && !confirm(`Re-attribute every booking for "${reattribStudent}" to ${reattribParentEmail}?`)) return;
    setReattribLoading(true);
    try {
      const res = await api.post('/admin/data/reattribute-bookings-by-name', {
        student_name: reattribStudent,
        parent_email: reattribParentEmail,
        dry_run: dryRun,
      });
      setReattribResult(res.data);
      toast.success(dryRun
        ? `Preview: ${res.data.bookings_found} booking(s) found, ${res.data.bookings_reattributed} would be re-attributed`
        : `Re-attributed ${res.data.bookings_reattributed} booking(s)`
      );
    } catch (error) {
      toast.error('Re-attribute failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setReattribLoading(false);
    }
  };

  const loadStripeRecon = async () => {
    setStripeReconLoading(true);
    try {
      const res = await api.get(`/admin/data/stripe-reconciliation?days=${reconDays}&min_amount=${reconMinAmount}`);
      setStripeRecon(res.data);
      const total = (res.data.summary?.not_in_db || 0) + (res.data.summary?.awaiting_authorization || 0)
        + (res.data.summary?.unlinked_user || 0) + (res.data.summary?.cross_linked_user || 0);
      toast.success(`Scanned ${res.data.summary?.total_pis_scanned} payments — ${total} flagged (${res.data.summary?.skipped_below_min || 0} small filtered out)`);
    } catch (error) {
      toast.error('Stripe scan failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setStripeReconLoading(false);
    }
  };

  const fixAwaiting = async (bookingId, piId) => {
    if (!confirm(`Flip booking ${bookingId.slice(0, 8)}… → paid using PI ${piId}?`)) return;
    setFixingPi(piId);
    try {
      await api.post('/admin/data/stripe-reconciliation/fix-awaiting', {
        booking_id: bookingId,
        payment_intent_id: piId,
      });
      toast.success('Booking marked paid');
      loadStripeRecon();
    } catch (error) {
      toast.error('Fix failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setFixingPi(null);
    }
  };

  const rebuildFromPi = async (piId, dryRun, manual = null) => {
    if (!dryRun && !confirm(`Rebuild missing booking(s) from PI ${piId}? This will create new booking record(s) marked paid.`)) return;
    setRebuildingPi(piId);
    try {
      const body = { payment_intent_id: piId, dry_run: dryRun };
      if (manual) {
        if (manual.student_name) body.manual_student_name = manual.student_name;
        if (manual.parent_email) body.manual_parent_email = manual.parent_email;
        if (manual.class_names) body.manual_class_names = manual.class_names;
      }
      const res = await api.post('/admin/data/stripe-reconciliation/rebuild-from-pi', body);
      const planned = res.data.planned || [];
      const willCreate = planned.filter(p => p.would_create).length;
      const exists = planned.filter(p => p.action === 'exists').length;
      if (dryRun) {
        toast.success(`Preview: ${willCreate} booking(s) would be created${exists ? `; ${exists} already exist` : ''}.`);
        const lines = [
          `Stripe customer: ${res.data.stripe_customer_email || res.data.stripe_customer_id || '— guest checkout'}`,
          `Student: ${res.data.metadata_student_name || '—'}`,
          `Classes: ${(res.data.metadata_class_names || []).join(', ') || '—'}`,
          `Total: £${res.data.amount_total}`,
          `Will create user: ${res.data.user_was_created ? 'YES' : (res.data.resolved_user_email ? `no (existing: ${res.data.resolved_user_email})` : 'no — manual review')}`,
          `Will create child: ${res.data.child_was_created ? 'YES' : 'no (already exists or no name)'}`,
          `Will repair cross-link: ${res.data.cross_link_repaired ? 'YES' : 'no'}`,
          `Will attach card: ${res.data.card_attached ? `YES (pm ${(res.data.real_payment_method_id || '').slice(0,16)}…)` : 'no — guest checkout PM not reusable'}`,
          `Bookings to create: ${willCreate}${exists ? ` (${exists} already exist)` : ''}`,
        ];
        // eslint-disable-next-line no-alert
        alert('Preview\n\n' + lines.join('\n'));
      } else {
        const created = [];
        if (res.data.user_was_created) created.push('parent user');
        if (res.data.child_was_created) created.push('child record');
        if (res.data.cross_link_repaired) created.push('repaired cross-link');
        if (res.data.card_attached) created.push('attached saved card');
        created.push(`${res.data.applied_count} booking(s)`);
        toast.success(`Created/repaired: ${created.join(', ')}`);
        loadStripeRecon();
        setManualForm({ piId: null, student_name: '', parent_email: '', class_names: '' });
      }
    } catch (error) {
      toast.error('Rebuild failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setRebuildingPi(null);
    }
  };

  // Manual rebuild form state (per PI)
  const [manualForm, setManualForm] = useState({ piId: null, student_name: '', parent_email: '', class_names: '' });
  const runDupCleanup = async (dryRun) => {
    if (!dryRun && !confirm('Merge duplicate children? Bookings from discarded rows will be moved onto the kept row, missing metadata will be copied over, then the extras will be deleted.')) return;
    setDupLoading(true);
    try {
      const res = await api.post('/admin/data/cleanup-duplicate-children', {
        dry_run: dryRun,
        keep_overrides: keepOverrides,
      });
      setDupResult(res.data);
      toast.success(dryRun ? `Found ${res.data.duplicate_groups?.length || 0} duplicate group(s)` : `Merged ${res.data.children_deleted || 0} duplicate(s)`);
      if (!dryRun) {
        runIntegrityCheck();
        setKeepOverrides({});
      }
    } catch (error) {
      toast.error('Cleanup failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setDupLoading(false);
    }
  };

  const runRestore = async (dryRun) => {
    // Strip accidental "student_id=" / "user_id=" / "id=" prefixes that users sometimes paste
    const stripPrefix = (v) => (v || '').trim().replace(/^\s*(student_id|user_id|id)\s*=\s*/i, '').trim();
    const sid = stripPrefix(restoreStudentId);
    const uid = stripPrefix(restoreUserId);
    const sname = stripPrefix(restoreStudentName);
    if (!sid || !uid) {
      toast.error('student_id and user_id are required');
      return;
    }
    if (!dryRun && !confirm('Apply restore? This will write to children and imported_students collections.')) return;
    setRestoring(true);
    try {
      const res = await api.post('/admin/data/restore-from-orphaned-bookings', {
        student_id: sid,
        user_id: uid,
        student_name: sname || undefined,
        dry_run: dryRun,
      });
      setRestoreResult(res.data);
      toast.success(dryRun ? 'Dry-run complete' : 'Restore applied');
    } catch (error) {
      toast.error('Restore failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setRestoring(false);
    }
  };

  useEffect(() => { runIntegrityCheck(); }, []);

  const runIntegrityCheck = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/data/integrity-check');
      setIntegrityResult(res.data);
    } catch (error) {
      toast.error('Failed to run integrity check');
    } finally {
      setLoading(false);
    }
  };

  const loadReconciliation = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/data/reconciliation');
      setReconciliation(res.data);
    } catch (error) {
      toast.error('Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  };

  const runRepair = async () => {
    if (!confirm('Run site-wide data repair? This will fix all broken parent-child linkages.')) return;
    setRepairing(true);
    try {
      const res = await api.post('/admin/data/repair-linkage');
      setRepairResult(res.data);
      toast.success(`Repair complete: ${res.data.summary?.children_repaired || 0} children fixed`);
      runIntegrityCheck();
      loadReconciliation();
    } catch (error) {
      toast.error('Repair failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setRepairing(false);
    }
  };

  const filtered = reconciliation?.records?.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return r.child_name.toLowerCase().includes(q) || r.parent_email.toLowerCase().includes(q);
  }) || [];

  return (
    <div className="space-y-6" data-testid="data-integrity-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-cyan-400" />
            Data Integrity
          </h2>
          <p className="text-sm text-white/50 mt-1">Parent-child linkage audit and repair</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runIntegrityCheck} disabled={loading} className="border-[#333] text-white hover:bg-[#222]" data-testid="run-integrity-check">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Check
          </Button>
          <Button variant="outline" onClick={loadReconciliation} disabled={loading} className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10" data-testid="load-reconciliation">
            <Users className="w-4 h-4 mr-2" />
            Audit
          </Button>
          <Button onClick={runRepair} disabled={repairing} className="bg-red-600 hover:bg-red-700" data-testid="run-repair">
            {repairing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            {repairing ? 'Repairing...' : 'Repair All'}
          </Button>
        </div>
      </div>

      {/* Integrity Check Results */}
      {integrityResult && (
        <Card className={`bg-[#111] ${integrityResult.overall === 'PASS' ? 'border-emerald-500/30' : 'border-red-500/30'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2">
              {integrityResult.overall === 'PASS'
                ? <CheckCircle className="w-5 h-5 text-emerald-400" />
                : <ShieldAlert className="w-5 h-5 text-red-400" />}
              Integrity: {integrityResult.overall}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {integrityResult.checks.map((check, i) => (
                <div key={i} className={`p-3 rounded-lg border ${
                  check.status === 'PASS' ? 'bg-emerald-500/5 border-emerald-500/20' :
                  check.status === 'WARN' ? 'bg-amber-500/5 border-amber-500/20' :
                  'bg-red-500/5 border-red-500/20'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/70">{check.check.replace(/_/g, ' ')}</span>
                    <Badge className={
                      check.status === 'PASS' ? 'bg-emerald-500/20 text-emerald-400' :
                      check.status === 'WARN' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    }>{check.count}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Repair Results */}
      {repairResult && (
        <Card className="bg-[#111] border-emerald-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base">Repair Report</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-white/70 space-y-1">
            <p>Children repaired: <span className="text-emerald-400 font-bold">{repairResult.summary?.children_repaired}</span></p>
            <p>Imported students repaired: <span className="text-emerald-400 font-bold">{repairResult.summary?.imported_students_repaired}</span></p>
            <p>Bookings repaired: <span className="text-emerald-400 font-bold">{repairResult.summary?.bookings_repaired}</span></p>
            <p>Booking user_id corrected: <span className="text-emerald-400 font-bold">{repairResult.summary?.bookings_user_id_corrected}</span></p>
            {repairResult.summary?.ambiguous_needing_manual_review > 0 && (
              <p className="text-amber-400">Ambiguous (manual review): {repairResult.summary.ambiguous_needing_manual_review}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Duplicate Children Cleanup */}
      <Card className="bg-[#111] border-rose-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Users className="w-5 h-5 text-rose-400" />
            Merge Duplicate Children
          </CardTitle>
          <p className="text-xs text-white/50 mt-1">
            Finds <code className="text-rose-300">children</code> rows sharing the same (normalized name, parent). Bookings from
            duplicates are re-pointed onto the kept row, empty metadata fields are filled in from the duplicates, then the extras are deleted.
            Default keep = oldest <code className="text-rose-300">created_at</code>. Tick a row to override.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Button
              data-testid="dup-dry-run-btn"
              variant="outline"
              onClick={() => runDupCleanup(true)}
              disabled={dupLoading}
              className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10"
            >
              {dupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Preview duplicates
            </Button>
            <Button
              data-testid="dup-apply-btn"
              onClick={() => runDupCleanup(false)}
              disabled={dupLoading || !dupResult?.duplicate_groups?.length}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {dupLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Apply merge
            </Button>
          </div>
          {dupResult && (
            <div className="mt-3 p-3 bg-[#0a0a0a] border border-[#222] rounded text-xs text-white/70">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <Badge className={dupResult.dry_run ? 'bg-rose-500/20 text-rose-400' : 'bg-emerald-500/20 text-emerald-400'}>
                  {dupResult.dry_run ? 'PREVIEW' : 'APPLIED'}
                </Badge>
                <span>Groups: <span className="text-white font-bold">{dupResult.duplicate_groups?.length || 0}</span></span>
                <span>Children: <span className="text-white font-bold">{dupResult.total_children_before}</span> → <span className="text-white font-bold">{dupResult.total_children_after}</span></span>
                {!dupResult.dry_run && (
                  <>
                    <span>Deleted: <span className="text-white font-bold">{dupResult.children_deleted}</span></span>
                    <span>Bookings repointed: <span className="text-white font-bold">{dupResult.bookings_repointed}</span></span>
                    <span>imported_students deleted: <span className="text-white font-bold">{dupResult.imported_students_deleted}</span></span>
                  </>
                )}
              </div>
              {dupResult.duplicate_groups?.length > 0 ? (
                <div className="space-y-3">
                  {dupResult.duplicate_groups.map((g, i) => {
                    const keepId = keepOverrides[g.group_key] || g.keep_id;
                    return (
                      <div key={i} className="border border-[#222] rounded p-3 bg-black/30">
                        <div className="text-white font-semibold mb-2">
                          {g.normalized_name}
                          <span className="text-white/40 font-mono text-[10px] ml-2">parent: {g.user_id.substring(0, 12)}...</span>
                        </div>
                        <table className="w-full text-[11px]">
                          <thead>
                            <tr className="text-white/40 border-b border-[#222]">
                              <th className="text-left py-1 px-2">Keep</th>
                              <th className="text-left py-1 px-2">ID</th>
                              <th className="text-left py-1 px-2">Raw name</th>
                              <th className="text-left py-1 px-2">Created</th>
                              <th className="text-center py-1 px-2">Bookings</th>
                              <th className="text-left py-1 px-2">Guardian2</th>
                              <th className="text-left py-1 px-2">Mailing</th>
                              <th className="text-left py-1 px-2">Birth</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.all_rows.map((r) => {
                              const isKeep = r.id === keepId;
                              return (
                                <tr key={r.id} className={`border-b border-[#1a1a1a] ${isKeep ? 'bg-emerald-500/5' : 'bg-rose-500/5'}`}>
                                  <td className="py-1 px-2">
                                    <input
                                      type="radio"
                                      name={`keep-${g.group_key}`}
                                      checked={isKeep}
                                      onChange={() => setKeepOverrides({ ...keepOverrides, [g.group_key]: r.id })}
                                      disabled={!dupResult.dry_run}
                                      data-testid={`keep-radio-${r.id}`}
                                    />
                                  </td>
                                  <td className="py-1 px-2 font-mono text-[10px] text-white/60">{r.id.substring(0, 12)}</td>
                                  <td className={`py-1 px-2 font-mono ${r.raw_name !== r.normalized_name.replace(/\b\w/g, c => c.toUpperCase()) ? 'text-amber-400' : 'text-white/80'}`}>
                                    "{r.raw_name}"
                                  </td>
                                  <td className="py-1 px-2 text-white/50">{(r.created_at || '').substring(0, 10)}</td>
                                  <td className="py-1 px-2 text-center text-white">{r.bookings}</td>
                                  <td className="py-1 px-2 text-white/60">{r.guardian2_email || r.guardian2_first_name || '—'}</td>
                                  <td className="py-1 px-2 text-white/60">{(r.mailing_lists || []).length ? (r.mailing_lists || []).join(', ') : '—'}</td>
                                  <td className="py-1 px-2 text-white/60">{r.birth_date || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {g.fields_to_copy?.length > 0 && (
                          <div className="mt-2 text-[10px] text-emerald-400/80">
                            Will copy into kept row: {g.fields_to_copy.map((f, k) => (
                              <span key={k} className="inline-block mr-2 bg-emerald-500/10 px-1.5 py-0.5 rounded">{f.field}{f.value ? `: ${String(f.value).substring(0, 30)}` : ''}{f.added ? `: +${f.added.length}` : ''}</span>
                            ))}
                          </div>
                        )}
                        {g.bookings_to_repoint > 0 && (
                          <div className="mt-1 text-[10px] text-cyan-400/80">
                            Will repoint <span className="font-bold">{g.bookings_to_repoint}</span> booking(s) onto kept row.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-emerald-400/70">No duplicates found.</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Orphaned Bookings */}
      <Card className="bg-[#111] border-red-500/30" data-testid="orphaned-bookings-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Delete Orphaned Bookings
          </CardTitle>
          <p className="text-xs text-white/50 mt-1">
            Bookings with empty <code className="text-red-300">student_id</code> or <code className="text-red-300">user_id</code> AND
            a <code className="text-red-300">student_name</code> that doesn't match any current child record.
            These are typically ghosts left behind after duplicate cleanups or deleted profiles.
            <span className="text-amber-300"> Always download the CSV first.</span>
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchOrphans}
              disabled={orphansLoading}
              className="border-[#333] text-white hover:bg-white/5"
              data-testid="find-orphans-btn"
            >
              {orphansLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Find orphaned bookings
            </Button>
            {orphans.length > 0 && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadOrphansCsv}
                  className="border-[#333] text-white hover:bg-white/5"
                  data-testid="download-orphans-csv"
                >
                  Download CSV ({orphans.length})
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={toggleAllOrphans}
                  className="border-[#333] text-white hover:bg-white/5"
                >
                  {orphanSelected.size === orphans.length ? 'Deselect all' : 'Select all'}
                </Button>
                <Button
                  size="sm"
                  onClick={deleteSelectedOrphans}
                  disabled={orphansDeleting || orphanSelected.size === 0}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="delete-orphans-btn"
                >
                  {orphansDeleting ? 'Deleting…' : `Delete selected (${orphanSelected.size})`}
                </Button>
              </>
            )}
          </div>

          {orphans.length > 0 && (
            <div className="border border-[#222] rounded-lg overflow-hidden max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0a0a0a] sticky top-0">
                  <tr className="text-left text-white/60">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2">Student</th>
                    <th className="px-3 py-2">Class</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {orphans.map((o) => (
                    <tr
                      key={o.id}
                      className="border-t border-[#222] hover:bg-white/5"
                      data-testid={`orphan-row-${o.id}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={orphanSelected.has(o.id)}
                          onChange={() => toggleOrphan(o.id)}
                          className="w-4 h-4 accent-red-500"
                        />
                      </td>
                      <td className="px-3 py-2 text-white">{o.student_name || <span className="text-white/40">—</span>}</td>
                      <td className="px-3 py-2 text-white/80">{o.class_name || <span className="text-white/40">—</span>}</td>
                      <td className="px-3 py-2 text-right text-white font-mono">£{Number(o.amount || 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-white/70">{o.payment_status}</td>
                      <td className="px-3 py-2 text-white/50 text-xs">{(o.created_at || '').slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restore from Orphaned Bookings */}
      <Card className="bg-[#111] border-amber-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Link2 className="w-5 h-5 text-amber-400" />
            Restore Student From Orphaned Bookings
          </CardTitle>
          <p className="text-xs text-white/50 mt-1">
            Rebuilds <code className="text-amber-300">children</code> + <code className="text-amber-300">imported_students</code> records
            for a student whose records were deleted but whose bookings still exist. Safe to re-run — it will not duplicate.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-white/50 block mb-1">Student ID (children.id = bookings.student_id)</label>
              <input
                data-testid="restore-student-id-input"
                type="text"
                value={restoreStudentId}
                onChange={(e) => setRestoreStudentId(e.target.value)}
                placeholder="uuid"
                className="w-full bg-[#0a0a0a] border border-[#333] rounded px-3 py-2 text-white text-sm font-mono placeholder:text-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1">Parent User ID (users.id)</label>
              <input
                data-testid="restore-user-id-input"
                type="text"
                value={restoreUserId}
                onChange={(e) => setRestoreUserId(e.target.value)}
                placeholder="uuid"
                className="w-full bg-[#0a0a0a] border border-[#333] rounded px-3 py-2 text-white text-sm font-mono placeholder:text-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1">Student Name (optional — derived from bookings)</label>
              <input
                data-testid="restore-student-name-input"
                type="text"
                value={restoreStudentName}
                onChange={(e) => setRestoreStudentName(e.target.value)}
                placeholder="e.g. Gustas Pratasius"
                className="w-full bg-[#0a0a0a] border border-[#333] rounded px-3 py-2 text-white text-sm placeholder:text-white/20"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="restore-dry-run-btn"
              variant="outline"
              onClick={() => runRestore(true)}
              disabled={restoring}
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
            >
              {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Preview (dry run)
            </Button>
            <Button
              data-testid="restore-apply-btn"
              onClick={() => runRestore(false)}
              disabled={restoring}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Apply Restore
            </Button>
          </div>
          {restoreResult && (
            <div className="mt-3 p-3 bg-[#0a0a0a] border border-[#222] rounded text-xs text-white/70">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={restoreResult.dry_run ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}>
                  {restoreResult.dry_run ? 'DRY RUN' : 'APPLIED'}
                </Badge>
                <span>Student: <span className="text-white">{restoreResult.student_name || '—'}</span></span>
                <span>Parent: <span className="text-white">{restoreResult.parent_email || '—'}</span></span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                <div>Bookings found: <span className="text-white font-bold">{restoreResult.bookings_found}</span></div>
                <div>Bookings linked: <span className="text-white font-bold">{restoreResult.final_state?.bookings_linked}</span></div>
                <div>Children present: <span className="text-white font-bold">{String(restoreResult.final_state?.children_present)}</span></div>
                <div>Imported present: <span className="text-white font-bold">{String(restoreResult.final_state?.imported_students_present)}</span></div>
              </div>
              {restoreResult.actions_planned?.length > 0 && (
                <div className="mb-2">
                  <div className="text-amber-400 font-semibold mb-1">Actions {restoreResult.dry_run ? 'planned' : 'taken'}:</div>
                  <pre className="bg-black/40 p-2 rounded overflow-x-auto text-[10px]">
                    {JSON.stringify(restoreResult.dry_run ? restoreResult.actions_planned : restoreResult.actions_taken, null, 2)}
                  </pre>
                </div>
              )}
              {restoreResult.warnings?.length > 0 && (
                <div>
                  <div className="text-amber-400 font-semibold mb-1">Warnings:</div>
                  <ul className="list-disc list-inside space-y-1">
                    {restoreResult.warnings.map((w, i) => (
                      <li key={i} className="text-white/60">{typeof w === 'string' ? w : JSON.stringify(w)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Re-attribute Bookings by Name */}
      <Card className="bg-[#111] border-pink-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Users className="w-5 h-5 text-pink-400" />
            Re-attribute Bookings by Student Name
          </CardTitle>
          <p className="text-xs text-white/50 mt-1">
            Finds every booking matching a student name and re-points them to the correct parent.
            Auto-creates the parent user (from Stripe customer) + child record + repairs cross-linked
            imported_students rows. Use this to fix Jessica McMurdy / Anabelle Geddes-style cases.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 block mb-1">Student name</label>
              <input
                data-testid="reattrib-student-name"
                type="text"
                value={reattribStudent}
                onChange={(e) => setReattribStudent(e.target.value)}
                placeholder="e.g. Jessica McMurdy"
                className="w-full bg-[#0a0a0a] border border-[#333] rounded px-3 py-2 text-white text-sm placeholder:text-white/20"
              />
            </div>
            <div>
              <label className="text-xs text-white/50 block mb-1">Correct parent email</label>
              <input
                data-testid="reattrib-parent-email"
                type="email"
                value={reattribParentEmail}
                onChange={(e) => setReattribParentEmail(e.target.value)}
                placeholder="e.g. amymcmurdy@live.co.uk"
                className="w-full bg-[#0a0a0a] border border-[#333] rounded px-3 py-2 text-white text-sm placeholder:text-white/20"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="reattrib-preview-btn"
              variant="outline"
              onClick={() => reattributeByName(true)}
              disabled={reattribLoading}
              className="border-pink-500/30 text-pink-400 hover:bg-pink-500/10"
            >
              {reattribLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Preview
            </Button>
            <Button
              data-testid="reattrib-apply-btn"
              onClick={() => reattributeByName(false)}
              disabled={reattribLoading}
              className="bg-pink-600 hover:bg-pink-700"
            >
              {reattribLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Apply
            </Button>
          </div>
          {reattribResult && (
            <div className="mt-3 p-3 bg-[#0a0a0a] border border-[#222] rounded text-xs text-white/70 space-y-2">
              <div className="flex gap-3 flex-wrap">
                <Badge className={reattribResult.dry_run ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}>
                  {reattribResult.dry_run ? 'DRY RUN' : 'APPLIED'}
                </Badge>
                <span>Bookings found: <span className="text-white font-bold">{reattribResult.bookings_found}</span></span>
                <span>{reattribResult.dry_run ? 'Would re-attribute' : 'Re-attributed'}: <span className="text-white font-bold">{reattribResult.bookings_reattributed}</span></span>
                <span>User created: <span className="text-white">{reattribResult.user_was_created ? 'YES' : 'no'}</span></span>
                <span>Child created: <span className="text-white">{reattribResult.child_was_created ? 'YES' : 'no'}</span></span>
                <span>Cross-links repaired: <span className="text-white">{reattribResult.cross_link_repaired}</span></span>
              </div>
              {reattribResult.bookings?.length > 0 && (
                <table className="w-full text-xs mt-2">
                  <thead className="text-white/40">
                    <tr>
                      <th className="text-left py-1">Booking</th>
                      <th className="text-left py-1">Amount</th>
                      <th className="text-left py-1">Class</th>
                      <th className="text-left py-1">Old user_id</th>
                      <th className="text-left py-1">Change needed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reattribResult.bookings.map((b) => (
                      <tr key={b.booking_id} className="border-t border-[#222]">
                        <td className="py-1 font-mono text-white/40">{(b.booking_id || '').slice(0, 12)}…</td>
                        <td className="py-1 text-white">£{(b.amount || 0).toFixed(2)}</td>
                        <td className="py-1 text-white">{b.class_name || '—'}</td>
                        <td className="py-1 font-mono text-white/40">{(b.old_user_id || 'EMPTY').slice(0, 12)}</td>
                        <td className="py-1 text-white">{b.needs_change ? <Badge className="bg-amber-500/20 text-amber-400">YES</Badge> : <Badge className="bg-emerald-500/20 text-emerald-400">no</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stripe Reconciliation */}
      <Card className="bg-[#111] border-purple-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-purple-400" />
            Stripe Reconciliation
          </CardTitle>
          <p className="text-xs text-white/50 mt-1">
            Walks every succeeded Stripe payment in the selected window and flags any that don't match a paid booking in our DB.
            Catches the same bug-pattern that affected Amy McMurdy / Anabelle Geddes / Ehime Chukwunweike.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2 items-center flex-wrap">
            <label className="text-xs text-white/50">Days back:</label>
            <select
              data-testid="stripe-recon-days"
              value={reconDays}
              onChange={(e) => setReconDays(parseInt(e.target.value))}
              className="bg-[#0a0a0a] border border-[#333] rounded px-3 py-1 text-white text-sm"
            >
              <option value={30}>30</option>
              <option value={60}>60</option>
              <option value={90}>90</option>
              <option value={180}>180</option>
              <option value={365}>365</option>
            </select>
            <label className="text-xs text-white/50 ml-2">Min £:</label>
            <input
              data-testid="stripe-recon-min-amount"
              type="number"
              min="0"
              step="1"
              value={reconMinAmount}
              onChange={(e) => setReconMinAmount(parseFloat(e.target.value) || 0)}
              className="bg-[#0a0a0a] border border-[#333] rounded px-2 py-1 text-white text-sm w-16"
              title="Filter out small card-auth payments below this threshold"
            />
            <Button
              data-testid="stripe-recon-run-btn"
              onClick={loadStripeRecon}
              disabled={stripeReconLoading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {stripeReconLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Run scan
            </Button>
          </div>

          {stripeRecon && (
            <div className="space-y-3 mt-3">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                <div className="p-2 bg-[#0a0a0a] border border-[#222] rounded">
                  <div className="text-white/50">Scanned</div>
                  <div className="text-white text-lg font-bold">{stripeRecon.summary.total_pis_scanned}</div>
                </div>
                <div className="p-2 bg-red-500/5 border border-red-500/20 rounded">
                  <div className="text-red-400/70">Not in DB</div>
                  <div className="text-red-400 text-lg font-bold">{stripeRecon.summary.not_in_db}</div>
                </div>
                <div className="p-2 bg-amber-500/5 border border-amber-500/20 rounded">
                  <div className="text-amber-400/70">Awaiting auth</div>
                  <div className="text-amber-400 text-lg font-bold">{stripeRecon.summary.awaiting_authorization}</div>
                </div>
                <div className="p-2 bg-orange-500/5 border border-orange-500/20 rounded">
                  <div className="text-orange-400/70">Unlinked</div>
                  <div className="text-orange-400 text-lg font-bold">{stripeRecon.summary.unlinked_user}</div>
                </div>
                <div className="p-2 bg-rose-500/5 border border-rose-500/20 rounded">
                  <div className="text-rose-400/70">Cross-linked</div>
                  <div className="text-rose-400 text-lg font-bold">{stripeRecon.summary.cross_linked_user}</div>
                </div>
              </div>

              {/* Awaiting authorization — auto-fixable */}
              {stripeRecon.issues.awaiting_authorization?.length > 0 && (
                <div className="border border-amber-500/30 rounded-lg p-3 bg-[#0a0a0a]">
                  <div className="text-sm text-amber-400 font-semibold mb-2">
                    {stripeRecon.issues.awaiting_authorization.length} booking(s) "awaiting_authorization" but Stripe payment succeeded — one-click fix
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-white/40">
                      <tr><th className="text-left py-1">Student</th><th className="text-left py-1">Amount</th><th className="text-left py-1">PI</th><th className="text-right py-1">Action</th></tr>
                    </thead>
                    <tbody>
                      {stripeRecon.issues.awaiting_authorization.map((row) => (
                        <tr key={row.payment_intent_id} className="border-t border-[#222]">
                          <td className="py-1 text-white">{row.booking_student_name}</td>
                          <td className="py-1 text-white">£{row.amount.toFixed(2)}</td>
                          <td className="py-1 font-mono text-white/40">{row.payment_intent_id.slice(0, 18)}…</td>
                          <td className="py-1 text-right">
                            <Button
                              size="sm"
                              data-testid={`fix-awaiting-${row.booking_id}`}
                              onClick={() => fixAwaiting(row.booking_id, row.payment_intent_id)}
                              disabled={fixingPi === row.payment_intent_id}
                              className="h-6 px-2 bg-emerald-600 hover:bg-emerald-700 text-xs"
                            >
                              {fixingPi === row.payment_intent_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Mark Paid'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Not in DB — needs manual rebuild */}
              {stripeRecon.issues.not_in_db?.length > 0 && (
                <div className="border border-red-500/30 rounded-lg p-3 bg-[#0a0a0a]">
                  <div className="text-sm text-red-400 font-semibold mb-2">
                    {stripeRecon.issues.not_in_db.length} succeeded Stripe payment(s) with no matching booking — manual rebuild required
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-white/40">
                      <tr><th className="text-left py-1">Created</th><th className="text-left py-1">Amount</th><th className="text-left py-1">Stripe customer</th><th className="text-left py-1">Student (metadata)</th><th className="text-left py-1">PI</th><th className="text-right py-1">Action</th></tr>
                    </thead>
                    <tbody>
                      {stripeRecon.issues.not_in_db.map((row) => {
                        const hasMetadata = row.metadata_student_name || row.metadata_booking_id;
                        const isManualOpen = manualForm.piId === row.payment_intent_id;
                        return (
                        <React.Fragment key={row.payment_intent_id}>
                        <tr className="border-t border-[#222]">
                          <td className="py-1 text-white/60">{row.created.slice(0, 10)}</td>
                          <td className="py-1 text-white">£{row.amount.toFixed(2)}</td>
                          <td className="py-1 font-mono text-white/40">{(row.stripe_customer_id || '—').slice(0, 18)}</td>
                          <td className="py-1 text-white">{row.metadata_student_name || '—'}</td>
                          <td className="py-1 font-mono text-white/40">{row.payment_intent_id.slice(0, 18)}…</td>
                          <td className="py-1 text-right">
                            {hasMetadata ? (
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid={`rebuild-preview-${row.payment_intent_id}`}
                                  onClick={() => rebuildFromPi(row.payment_intent_id, true)}
                                  disabled={rebuildingPi === row.payment_intent_id}
                                  className="h-6 px-2 text-xs border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                                >
                                  {rebuildingPi === row.payment_intent_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Preview'}
                                </Button>
                                <Button
                                  size="sm"
                                  data-testid={`rebuild-${row.payment_intent_id}`}
                                  onClick={() => rebuildFromPi(row.payment_intent_id, false)}
                                  disabled={rebuildingPi === row.payment_intent_id}
                                  className="h-6 px-2 bg-purple-600 hover:bg-purple-700 text-xs"
                                >
                                  Rebuild
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`rebuild-manual-${row.payment_intent_id}`}
                                onClick={() => setManualForm(isManualOpen
                                  ? { piId: null, student_name: '', parent_email: '', class_names: '' }
                                  : { piId: row.payment_intent_id, student_name: '', parent_email: '', class_names: '' })}
                                className="h-6 px-2 text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                              >
                                {isManualOpen ? 'Cancel' : 'Manual…'}
                              </Button>
                            )}
                          </td>
                        </tr>
                        {isManualOpen && (
                          <tr>
                            <td colSpan={6} className="py-2 px-3 bg-[#0e0e0e] border-t border-amber-500/20">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                                <input
                                  type="text"
                                  placeholder="Student name (e.g. Anabelle Geddes)"
                                  value={manualForm.student_name}
                                  onChange={(e) => setManualForm({ ...manualForm, student_name: e.target.value })}
                                  className="bg-[#0a0a0a] border border-[#333] rounded px-2 py-1 text-white placeholder:text-white/20"
                                  data-testid={`manual-student-${row.payment_intent_id}`}
                                />
                                <input
                                  type="email"
                                  placeholder="Parent email"
                                  value={manualForm.parent_email}
                                  onChange={(e) => setManualForm({ ...manualForm, parent_email: e.target.value })}
                                  className="bg-[#0a0a0a] border border-[#333] rounded px-2 py-1 text-white placeholder:text-white/20"
                                  data-testid={`manual-email-${row.payment_intent_id}`}
                                />
                                <input
                                  type="text"
                                  placeholder="Classes (comma-separated, optional)"
                                  value={manualForm.class_names}
                                  onChange={(e) => setManualForm({ ...manualForm, class_names: e.target.value })}
                                  className="bg-[#0a0a0a] border border-[#333] rounded px-2 py-1 text-white placeholder:text-white/20"
                                  data-testid={`manual-classes-${row.payment_intent_id}`}
                                />
                              </div>
                              <div className="flex gap-2 mt-2 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid={`manual-preview-${row.payment_intent_id}`}
                                  onClick={() => rebuildFromPi(row.payment_intent_id, true, manualForm)}
                                  disabled={rebuildingPi === row.payment_intent_id || !manualForm.student_name || !manualForm.parent_email}
                                  className="h-7 px-3 text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                                >
                                  Preview
                                </Button>
                                <Button
                                  size="sm"
                                  data-testid={`manual-rebuild-${row.payment_intent_id}`}
                                  onClick={() => rebuildFromPi(row.payment_intent_id, false, manualForm)}
                                  disabled={rebuildingPi === row.payment_intent_id || !manualForm.student_name || !manualForm.parent_email}
                                  className="h-7 px-3 bg-amber-600 hover:bg-amber-700 text-xs"
                                >
                                  Rebuild
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );})}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Unlinked user / student */}
              {stripeRecon.issues.unlinked_user?.length > 0 && (
                <div className="border border-orange-500/30 rounded-lg p-3 bg-[#0a0a0a]">
                  <div className="text-sm text-orange-400 font-semibold mb-2">
                    {stripeRecon.issues.unlinked_user.length} paid booking(s) missing user_id/student_id — won't appear on student profiles
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-white/40">
                      <tr><th className="text-left py-1">Student</th><th className="text-left py-1">Amount</th><th className="text-left py-1">user_id</th><th className="text-left py-1">student_id</th><th className="text-left py-1">PI</th></tr>
                    </thead>
                    <tbody>
                      {stripeRecon.issues.unlinked_user.map((row) => (
                        <tr key={row.payment_intent_id} className="border-t border-[#222]">
                          <td className="py-1 text-white">{row.booking_student_name}</td>
                          <td className="py-1 text-white">£{row.amount.toFixed(2)}</td>
                          <td className="py-1 font-mono text-orange-400/70">{row.booking_user_id || 'EMPTY'}</td>
                          <td className="py-1 font-mono text-orange-400/70">{row.booking_student_id || 'EMPTY'}</td>
                          <td className="py-1 font-mono text-white/40">{row.payment_intent_id.slice(0, 18)}…</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-white/40 mt-2">
                    Use the "Restore Student From Orphaned Bookings" tool above with the booking's <code>user_id</code>/<code>student_id</code> to repair the linkage.
                  </p>
                </div>
              )}

              {/* Cross-linked user */}
              {stripeRecon.issues.cross_linked_user?.length > 0 && (
                <div className="border border-rose-500/30 rounded-lg p-3 bg-[#0a0a0a]">
                  <div className="text-sm text-rose-400 font-semibold mb-2">
                    {stripeRecon.issues.cross_linked_user.length} booking(s) where the Stripe customer doesn't match the parent's saved customer — likely cross-linked!
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-white/40">
                      <tr><th className="text-left py-1">Student</th><th className="text-left py-1">Amount</th><th className="text-left py-1">Booking parent</th><th className="text-left py-1">PI customer</th><th className="text-left py-1">Expected customer</th></tr>
                    </thead>
                    <tbody>
                      {stripeRecon.issues.cross_linked_user.map((row) => (
                        <tr key={row.payment_intent_id} className="border-t border-[#222]">
                          <td className="py-1 text-white">{row.booking_student_name}</td>
                          <td className="py-1 text-white">£{row.amount.toFixed(2)}</td>
                          <td className="py-1 text-white/60">{row.parent_email || '—'}</td>
                          <td className="py-1 font-mono text-rose-400/70">{(row.stripe_customer_id || '—').slice(0, 18)}</td>
                          <td className="py-1 font-mono text-rose-300">{(row.expected_stripe_customer_id || '—').slice(0, 18)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {Object.values(stripeRecon.summary).every(v => typeof v !== 'number' || ['total_pis_scanned', 'days'].includes(Object.keys(stripeRecon.summary).find(k => stripeRecon.summary[k] === v)) || v === 0) && (
                <div className="text-center text-emerald-400 py-3 text-sm">
                  ✓ All Stripe payments reconcile cleanly with the DB.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reconciliation Table */}
      {reconciliation && (
        <>
          <div className="flex items-center gap-4">
            <div className="flex gap-3">
              <Badge className="bg-emerald-500/20 text-emerald-400">Healthy: {reconciliation.healthy}</Badge>
              <Badge className="bg-red-500/20 text-red-400">Broken: {reconciliation.broken}</Badge>
            </div>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#111] border border-[#333] rounded-lg pl-10 pr-10 py-2 text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#333] text-white/50">
                  <th className="text-left py-2 px-3">Student</th>
                  <th className="text-left py-2 px-3">Parent</th>
                  <th className="text-left py-2 px-3">children.user_id</th>
                  <th className="text-left py-2 px-3">imported.user_id</th>
                  <th className="text-center py-2 px-3">Bookings</th>
                  <th className="text-center py-2 px-3">Stripe</th>
                  <th className="text-left py-2 px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} className={`border-b border-[#222] ${r.linkage_status === 'BROKEN' ? 'bg-red-500/5' : ''}`}>
                    <td className="py-2 px-3 text-white">{r.child_name}</td>
                    <td className="py-2 px-3 text-white/60 text-xs">{r.parent_email || r.parent_name || '-'}</td>
                    <td className="py-2 px-3 font-mono text-xs text-white/40">{r.children_user_id ? r.children_user_id.substring(0, 12) + '...' : 'EMPTY'}</td>
                    <td className="py-2 px-3 font-mono text-xs text-white/40">{r.imported_students_user_id === 'N/A' ? '-' : (r.imported_students_user_id || 'EMPTY').substring(0, 12)}</td>
                    <td className="py-2 px-3 text-center">{r.bookings_count}</td>
                    <td className="py-2 px-3 text-center">{r.has_stripe ? <CreditCard className="w-4 h-4 text-emerald-400 mx-auto" /> : <X className="w-4 h-4 text-red-400/40 mx-auto" />}</td>
                    <td className="py-2 px-3">
                      {r.linkage_status === 'OK'
                        ? <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">OK</Badge>
                        : <div>
                            <Badge className="bg-red-500/20 text-red-400 text-xs">BROKEN</Badge>
                            <div className="text-xs text-red-400/60 mt-1">{r.issues.join(', ')}</div>
                          </div>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Orphan Stripe Subscriptions */}
      <Card className="bg-[#0a0a0a] border-[#222]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-amber-400" />
            Orphan Stripe Subscriptions
          </CardTitle>
          <p className="text-white/60 text-sm">
            Active subscriptions on Stripe that have NO matching booking in our DB. Use when a payment was charged
            but the original "Take Payment" call crashed before finishing.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={fetchOrphanSubs}
            disabled={orphanSubsLoading}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="scan-orphan-subs-btn"
          >
            {orphanSubsLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</> : <><Search className="w-4 h-4 mr-2" /> Scan Stripe</>}
          </Button>

          {orphanSubs && (
            <div className="space-y-3">
              <p className="text-white/70 text-sm">
                Found <strong className="text-white">{orphanSubs.count}</strong> orphan subscription(s) (last 100 active scanned).
              </p>
              {orphanSubs.orphans.length > 0 && (
                <div className="space-y-3">
                  {orphanSubs.orphans.map((o) => {
                    const metaBookingIds = (o.metadata?.booking_ids || '')
                      .split(',').map((s) => s.trim()).filter(Boolean);
                    const metaStudent = o.metadata?.student_name;
                    return (
                      <div
                        key={o.subscription_id}
                        className="rounded border border-[#222] bg-[#0a0a0a] p-3 space-y-2"
                        data-testid={`orphan-card-${o.subscription_id}`}
                      >
                        <div className="flex items-start justify-between flex-wrap gap-3">
                          <div className="min-w-0">
                            <div className="text-white font-medium">
                              {metaStudent || o.customer_name || '—'}
                              <span className="text-white/50 font-normal text-sm ml-2">
                                {o.metadata?.class_names ? `· ${o.metadata.class_names}` : ''}
                              </span>
                            </div>
                            <div className="text-xs text-white/50">
                              {o.customer_name} · {o.customer_email}
                            </div>
                            <div className="text-xs text-white/40 mt-1 font-mono">{o.subscription_id}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-pink-400 font-semibold">£{Number(o.amount).toFixed(2)}/mo</div>
                            <div className="text-xs text-white/40">{new Date(o.created).toLocaleString('en-GB')}</div>
                          </div>
                        </div>

                        {/* Auto-link from Stripe metadata */}
                        {metaBookingIds.length > 0 && (
                          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 flex items-center justify-between gap-3">
                            <div className="text-xs text-emerald-300">
                              ✓ Booking ID found in Stripe metadata:{' '}
                              <span className="font-mono text-white">{metaBookingIds[0]}</span>
                            </div>
                            <Button
                              size="sm"
                              onClick={async () => {
                                setLinkSubSaving(true);
                                try {
                                  const res = await api.post('/payments/subscriptions/link-to-booking', {
                                    booking_id: metaBookingIds[0],
                                    stripe_subscription_id: o.subscription_id,
                                  });
                                  toast.success(res.data.message || 'Linked!');
                                  fetchOrphanSubs();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Link failed');
                                } finally {
                                  setLinkSubSaving(false);
                                }
                              }}
                              disabled={linkSubSaving}
                              className="bg-emerald-600 hover:bg-emerald-700 h-7 px-3 text-xs"
                              data-testid={`auto-link-${o.subscription_id}`}
                            >
                              {linkSubSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Auto-link'}
                            </Button>
                          </div>
                        )}

                        {/* Candidate matches by parent email */}
                        {o.candidate_bookings.length > 0 && (
                          <div className="space-y-1">
                            <div className="text-xs text-white/60">Candidate bookings (same parent):</div>
                            {o.candidate_bookings.map((c) => (
                              <div
                                key={c.id}
                                className="flex items-center justify-between bg-[#1a1a1a] rounded px-2 py-1 text-xs"
                              >
                                <div className="text-white/80">
                                  {c.student_name} · {c.class_name} ·{' '}
                                  <span className="text-white/50">£{Number(c.amount || 0).toFixed(2)}</span>{' '}
                                  <span className="text-amber-400">[{c.payment_status}]</span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    setLinkSubSaving(true);
                                    try {
                                      const res = await api.post('/payments/subscriptions/link-to-booking', {
                                        booking_id: c.id,
                                        stripe_subscription_id: o.subscription_id,
                                      });
                                      toast.success(res.data.message || 'Linked!');
                                      fetchOrphanSubs();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Link failed');
                                    } finally {
                                      setLinkSubSaving(false);
                                    }
                                  }}
                                  disabled={linkSubSaving}
                                  className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 h-6 px-2 text-xs"
                                >
                                  Link
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}

                        {metaBookingIds.length === 0 && o.candidate_bookings.length === 0 && (
                          <div className="text-xs text-white/40">
                            No booking ID in metadata and no matching pending bookings found.
                            Use the manual form below.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="border-t border-[#222] pt-4 space-y-3">
            <h4 className="text-white font-medium">Link existing Stripe subscription to a booking</h4>
            <p className="text-white/60 text-xs">
              Click a row above to auto-fill the subscription ID, then paste the booking ID from the Take Payments tab.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                type="text"
                value={linkSub.booking_id}
                onChange={(e) => setLinkSub({ ...linkSub, booking_id: e.target.value })}
                placeholder="Booking ID"
                className="bg-[#111] border border-[#333] text-white rounded px-3 py-2 font-mono text-sm"
                data-testid="link-booking-id"
              />
              <input
                type="text"
                value={linkSub.stripe_subscription_id}
                onChange={(e) => setLinkSub({ ...linkSub, stripe_subscription_id: e.target.value })}
                placeholder="sub_xxxxxxxxxxxxxx"
                className="bg-[#111] border border-[#333] text-white rounded px-3 py-2 font-mono text-sm"
                data-testid="link-sub-id"
              />
            </div>
            <Button
              onClick={submitLinkSub}
              disabled={linkSubSaving || !linkSub.booking_id.trim() || !linkSub.stripe_subscription_id.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="link-sub-submit"
            >
              {linkSubSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Linking…</> : <><Link2 className="w-4 h-4 mr-2" /> Link Subscription</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* DIAGNOSE STUDENT VISIBILITY */}
      <Card className="bg-[#0e0e15] border-[#7000FF]/40">
        <CardHeader className="border-b border-[#7000FF]/30">
          <CardTitle className="text-white flex items-center gap-2">
            <Search className="w-5 h-5 text-[#B388FF]" />
            Diagnose Student Visibility
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <p className="text-white/60 text-sm">
            Paste a <span className="font-mono text-white/80">child_id</span> (UUID) to see the full DB picture
            and find out why a student isn't appearing in Students / Renewals.
          </p>

          <div className="p-3 bg-amber-900/20 border border-amber-500/40 rounded text-amber-200/90 text-xs flex items-center justify-between gap-3">
            <div>
              <strong>One-click data fix:</strong> if Diagnose shows a TypeError about <span className="font-mono">'int' and 'NoneType'</span>,
              run this to set <span className="font-mono">amount=0</span> on any bookings where amount is null. Required to recover Grace.
            </div>
            <Button
              onClick={runFixNulls}
              disabled={fixNullsLoading}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 whitespace-nowrap"
              data-testid="fix-null-amounts-btn"
            >
              {fixNullsLoading ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : null}
              Fix null amounts
            </Button>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={diagInput}
              onChange={(e) => setDiagInput(e.target.value)}
              placeholder="d0f8cb7c-4b40-4d04-afab-f9164d3159b9"
              className="flex-1 bg-[#0a0a0a] border border-[#333] rounded px-3 py-2 text-white text-sm font-mono placeholder:text-white/20"
              data-testid="diagnose-input"
            />
            <Button
              onClick={runDiagnose}
              disabled={diagLoading}
              className="bg-[#7000FF] hover:bg-[#5a00cc]"
              data-testid="diagnose-submit"
            >
              {diagLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Diagnose
            </Button>
          </div>
          {diagResult && !diagResult.error && (
            <div className="mt-3 p-3 bg-[#0a0a0a] border border-[#222] rounded space-y-2 text-xs">
              {!diagResult.found_in_children ? (
                <div className="text-red-300 font-semibold">
                  ❌ No child record found for ID {diagResult.child_id}
                </div>
              ) : (
                <>
                  <div className="text-white/80">
                    <strong>Explanation:</strong> {diagResult.explanation}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-2 text-white/70">
                    <div className="bg-[#050505] p-2 rounded">
                      <div className="text-white/40 mb-1">Child record</div>
                      <div>name: <span className="text-white">{diagResult.child?.name}</span></div>
                      <div>status: <span className="text-amber-300 font-mono">{JSON.stringify(diagResult.child?.status)}</span></div>
                      <div>archived_at: <span className="text-amber-300 font-mono">{JSON.stringify(diagResult.child?.archived_at)}</span></div>
                      <div>user_id: <span className="font-mono">{diagResult.child?.user_id || '∅'}</span></div>
                      <div>birth_date: <span className="font-mono">{diagResult.child?.birth_date || '∅'}</span></div>
                    </div>
                    <div className="bg-[#050505] p-2 rounded">
                      <div className="text-white/40 mb-1">Parent record</div>
                      <div>email: <span className="text-white">{diagResult.parent?.email || '∅'}</span></div>
                      <div>role: <span className="text-amber-300 font-mono">{JSON.stringify(diagResult.parent?.role)}</span></div>
                      <div>parent_role_ok: <span className={diagResult.parent_role_ok ? 'text-emerald-300' : 'text-red-300'}>{String(diagResult.parent_role_ok)}</span></div>
                      <div>id: <span className="font-mono">{diagResult.parent?.id || '∅'}</span></div>
                    </div>
                  </div>
                  <div className="text-white/70 mt-2">
                    <strong>Bookings:</strong> {diagResult.booking_count}
                  </div>
                  <details>
                    <summary className="cursor-pointer text-white/60 hover:text-white">Full JSON</summary>
                    <pre className="mt-2 p-2 bg-[#050505] text-white/70 overflow-x-auto text-[10px] leading-tight">{JSON.stringify(diagResult, null, 2)}</pre>
                  </details>
                </>
              )}
            </div>
          )}
          {diagResult?.error && (
            <div className="mt-3 p-3 bg-red-900/20 border border-red-500/40 rounded text-red-300 text-sm">
              {diagResult.error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SILENT IMPORTED-STUDENT RECOVERY */}
      <Card className="bg-[#0e0e15] border-[#7000FF]/40">
        <CardHeader className="border-b border-[#7000FF]/30">
          <CardTitle className="text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#B388FF]" />
            Silent Recovery — Vanished Imported Student
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <p className="text-white/60 text-sm">
            Use when a student paid via the imported-student flow but their record has
            since vanished from Students / Renewals / Take Payments. This rebuilds the
            missing <span className="font-mono text-white/80">users</span> + <span className="font-mono text-white/80">children</span> + <span className="font-mono text-white/80">bookings</span> rows
            from the saved pending_enrollment and Stripe payment history.
            <strong className="block text-amber-300 mt-2">No emails or notifications are sent to the parent.</strong>
          </p>

          <div className="grid sm:grid-cols-3 gap-2">
            <div>
              <label className="block text-xs text-white/50 mb-1">pending_enrollment_id</label>
              <input
                type="text"
                value={recPe}
                onChange={(e) => setRecPe(e.target.value)}
                placeholder="cbb9f615-c25d-…"
                className="w-full bg-[#0a0a0a] border border-[#333] rounded px-2 py-1.5 text-white text-xs font-mono placeholder:text-white/20"
                data-testid="recover-pe-input"
              />
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1">stripe_customer_id</label>
              <input
                type="text"
                value={recCus}
                onChange={(e) => setRecCus(e.target.value)}
                placeholder="cus_…"
                className="w-full bg-[#0a0a0a] border border-[#333] rounded px-2 py-1.5 text-white text-xs font-mono placeholder:text-white/20"
                data-testid="recover-cus-input"
              />
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1">stripe_payment_intent_id</label>
              <input
                type="text"
                value={recPi}
                onChange={(e) => setRecPi(e.target.value)}
                placeholder="pi_…"
                className="w-full bg-[#0a0a0a] border border-[#333] rounded px-2 py-1.5 text-white text-xs font-mono placeholder:text-white/20"
                data-testid="recover-pi-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
            <label className="inline-flex items-center gap-2 text-sm text-white/70">
              <input
                type="checkbox"
                checked={recDryRun}
                onChange={(e) => setRecDryRun(e.target.checked)}
                className="accent-[#FF007F]"
                data-testid="recover-dryrun-toggle"
              />
              Dry run (preview what would change, no DB writes)
            </label>
            <Button
              onClick={recoverImportedStudent}
              disabled={recRunning}
              className={recDryRun ? 'bg-[#7000FF] hover:bg-[#5a00cc]' : 'bg-emerald-600 hover:bg-emerald-700'}
              data-testid="recover-submit-btn"
            >
              {recRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</> : (recDryRun ? 'Run Dry Run' : 'Recover Student')}
            </Button>
          </div>

          {recResult && !recResult.error && (
            <div className="mt-4 p-3 bg-[#0a0a0a] border border-[#222] rounded space-y-2 text-xs">
              <div className="flex flex-wrap items-center gap-3 text-white/80">
                <span className="px-2 py-0.5 rounded bg-emerald-700/40 text-emerald-300">
                  {recResult.dry_run ? 'DRY RUN' : 'EXECUTED'}
                </span>
                <span><strong>Student:</strong> {recResult.student_name}</span>
                <span><strong>Parent:</strong> {recResult.user_email}</span>
                <span><strong>Bookings created:</strong> {recResult.bookings_created}</span>
              </div>
              {recResult.warnings?.length > 0 && (
                <div className="text-amber-300">
                  {recResult.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
              <details className="mt-2">
                <summary className="cursor-pointer text-white/60 hover:text-white">Actions taken ({recResult.actions?.length || 0})</summary>
                <pre className="mt-2 p-2 bg-[#050505] text-white/70 overflow-x-auto text-[10px] leading-tight">{JSON.stringify(recResult.actions, null, 2)}</pre>
              </details>
            </div>
          )}
          {recResult?.error && (
            <div className="mt-4 p-3 bg-red-900/20 border border-red-500/40 rounded text-red-300 text-sm">
              {recResult.error}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DataIntegrityTab;
